<template>
    <div class="unsplash-picker">
        <div class="form">
            <div class="columns is-mobile is-gapless">
                <div class="column">
                    <div class="search-input input-group">
                        <input type="search" class="input"
                            v-model="unsplashSearchTerm"
                            v-on:keydown.enter.prevent="searchUnsplashImages"
                            ref="unsplashSearch"
                            :placeholder="searchPlaceholder">
                        <div class="addon">
                            <button id="#images-search-button"
                                :aria-label="$t__('Search')"
                                class="btn-link grey-light"
                                @click.prevent="searchUnsplashImages"
                                ref="searchButton">
                                <i class="icon search" aria-labelledby="#images-search-button"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M8,15c-3.9,0-7-3.1-7-7s3.1-7,7-7s7,3.1,7,7S11.9,15,8,15z M8,3C5.2,3,3,5.2,3,8s2.2,5,5,5s5-2.2,5-5S10.8,3,8,3z" class="primary"></path><path d="M17.3,18.7l-3-3c-0.4-0.4-0.4-1,0-1.4s1-0.4,1.4,0l3,3c0.4,0.4,0.4,1,0,1.4C18.3,19.1,17.7,19.1,17.3,18.7z" class="secondary"></path></svg></i>
                            </button>
                        </div>
                    </div>
                </div>

                <div class="column is-narrow">
                    <button id="#images-history-button"
                        @click.prevent="browseHistory"
                        :aria-label="$t__('History')"
                        :class="['btn btn-history regular-padding', {'grey-light': !historyIsOpened, 'dark': historyIsOpened}]"
                        ref="historyButton">
                        <svg-icon name="time" :class="[{'current-color': historyIsOpened}]" aria-labelledby="#images-history-button"></svg-icon>
                    </button>
                </div>
            </div>

        </div>

        <div @scroll.stop="onScroll" ref="scroll-panel" :class="['scroll-panel', {'is-animating': swappingScreens}]">
            <div ref="masonry" class="masonry lb-gallery images" v-show="currentScreen == 'search' || currentScreen == 'history'">
                <div class="masonry-grid-sizer"></div>
                <div class="masonry-gutter-sizer"></div>

                <div v-for="(image, index) in unsplashImages" :key="`${image.id}-${index}`" :class="['masonry-item image', {'is-active': isSelected(image)}]">
                    <div class="img"
                        :style="`padding-top: ${getImageRatio(image)}`"
                    >
                        <img :class="{selected: isSelected(image)}"
                        :src='`${image.urls.raw}&w=400&dpi=1&fm=jpg&q=80&crop=entropy&cs=tinysrgb&fit=max`' />

                        <div class="overlay" @click="select(image)">
                            <div class="checkbox"></div>
                            <a :href="`${image.urls.full}`"
                                class="lb-item show-detail"
                                :data-size="`${image.width}x${image.height}`"
                                :data-thumb="`${image.urls.raw}&w=400&dpi=1&fm=jpg&q=80&crop=entropy&cs=tinysrgb&fit=max`"
                                :data-title="$t__(
                                    'Photo by %s via %s',
                                    `<a href='${image.user.links.html}?utm_source=Pixenio&utm_medium=referral' target='_blank'>${image.user.name}</a>`,
                                    `<a href='unsplash.com' target='_blank'>Unsplash</a>`
                                )"
                                @click.stop>
                                <i class="icon search color-white"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M8,15c-3.9,0-7-3.1-7-7s3.1-7,7-7s7,3.1,7,7S11.9,15,8,15z M8,3C5.2,3,3,5.2,3,8s2.2,5,5,5s5-2.2,5-5S10.8,3,8,3z" class="primary"></path><path d="M17.3,18.7l-3-3c-0.4-0.4-0.4-1,0-1.4s1-0.4,1.4,0l3,3c0.4,0.4,0.4,1,0,1.4C18.3,19.1,17.7,19.1,17.3,18.7z" class="secondary"></path></svg></i>
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            <div v-show="currentScreen == 'no-search-results'" class="empty-screen">
                <div class="empty-screen-wrap">
                    <div class="media">
                        <i class="icon image-cross"><svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg" fill-rule="evenodd" clip-rule="evenodd" stroke-linejoin="round" stroke-miterlimit="1.41"><path class="primary" d="M19 20H1c-.6 0-1-.4-1-1V1c0-.6.4-1 1-1h18c.6 0 1 .4 1 1v18c0 .6-.4 1-1 1zM2 18h16V2H2v16z" fill-rule="nonzero"/><path class="secondary" d="M10.98 4.57l.7-.71.12-.1c.2-.15.45-.22.7-.2a1.02 1.02 0 0 1 .9.89v.22l-.01.08a1 1 0 0 1-.18.4l-.1.12-.7.72.73.72.1.11c.19.26.25.62.14.93-.1.3-.37.55-.69.64-.3.09-.66.01-.91-.2l-.06-.05L11 7.4l-.7.72-.12.1c-.26.2-.61.25-.93.14-.3-.1-.55-.36-.64-.68-.08-.32 0-.67.2-.92l.06-.05.7-.72-.7-.7-.1-.1a1.01 1.01 0 0 1 .34-1.5c.1-.05.23-.09.35-.1h.3a1 1 0 0 1 .41.18l.12.1.7.7z" fill-rule="nonzero"/><path class="primary" d="M3.3 15.7c-.4-.4-.4-1 0-1.4l4-4c.4-.4.9-.4 1.3-.1l3.3 2.5 3.4-3.4c.4-.4 1-.4 1.4 0 .4.4.4 1 0 1.4l-4 4c-.4.4-.9.4-1.3.1l-3.3-2.5-3.4 3.4c-.4.4-1 .4-1.4 0z" fill-rule="nonzero"/></svg></i>
                    </div>

                    <div class="message">
                        <h2 class="title weight-normal">{{ $c__('searched image', 'We haven’t found this one') }}</h2>
                        {{ noResultsMessage }}
                    </div>
                </div>
            </div>

            <div v-show="currentScreen == 'welcome'" class="empty-screen">
                <div class="empty-screen-wrap">
                    <div class="media">
                        <svg-icon name="image"></svg-icon>
                    </div>

                    <div class="message">
                        <h2 class="title weight-normal">{{ $t__('Search free images') }}</h2>
                        {{ $c__('empty screen with search input on top without autosubmit', 'Use input above and hit enter') }}
                    </div>
                </div>
            </div>

            <div v-show="currentScreen == 'no-history'" class="empty-screen">
                <div class="empty-screen-wrap">
                    <div class="media">
                        <svg-icon name="time"></svg-icon>
                    </div>

                    <div class="message">
                        <h2 class="title weight-normal">{{ $t__('Used images history') }}</h2>
                        {{ $t__('Here will be your last used images') }}
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import * as utils from '../support/utils';
import uniqBy from 'lodash.uniqby';
import LocalStorage from '../support/LocalStorage';


export default {
    name: 'UnsplashPicker',

    props: {
        multiselect: {type: Boolean, default: false}
    },

    data() {
        return {
            unsplashImages: [],
            unsplashSearchTerm: '',
            page: 1,
            selectedUnsplashImages: [],
            searchResults: [],
            history: [],
            translations: {},
            masonry: null,
            lightbox: null,
            currentScreen: 'welcome', // welcome, search, history, no-search-results, no-history
            swappingScreens: false,
            atBottom: false,
            lastScrollPositionBeforeSwap: 0
        }
    },

    computed: {
        searchPlaceholder() {
            return this.translationByLocale(this.translations.search, this.translations.searchInLanguage);
        },

        noResultsMessage() {
            return this.translationByLocale(this.translations.noResultsMessage, this.translations.noResultsMessageInLanguage);
        },

        firstTimeOpened() {
            return this.unsplashSearchTerm == '' && this.unsplashImages.length == 0;
        },

        historyIsOpened() {
            return this.currentScreen == 'history' || this.currentScreen == 'no-history';
        },
    },

    watch: {
        selectedUnsplashImages() {
            this.$emit('selected-images', this.selectedUnsplashImages, this.rememberUsedImages);
        },
    },

    mounted() {
        this.translations = {
            search: "Search e.g. landscape",
            searchInLanguage: this.$t__('Landscape or %s', 'landscape'),
            noResultsMessage: 'Try with different word',
            noResultsMessageInLanguage: this.$c__('after search with no results', 'Try with different word or use english'),
        }
        this.loadFromLocalStorage();
        this.loadHistoryFromLocalStorage();
    },

    methods: {
        myCallback() {
            return function(){console.log('wtf')};
        },

        clearSelection() {
            this.selectedUnsplashImages = [];
        },

        isSelected(image) {
            return this.selectedUnsplashImages.includes(image);
        },

        select(image) {
            if (!this.selectedUnsplashImages.includes(image)) {
                if (!this.multiselect) this.selectedUnsplashImages = [];
                this.selectedUnsplashImages.push(image);
            } else {
                this.selectedUnsplashImages.splice( this.selectedUnsplashImages.indexOf(image), 1 );
            }
        },

        searchUnsplashImages() {
            if (this.unsplashSearchTerm == '') return;

            this.page = 1;
            this.$refs.searchButton.classList.add('is-loading');
            // axios endpoint is different in site wizard. See SiteWizard.js
            axios.get(`${this.$evilCorp.axiosEndpoint}/unsplash`, {
                params: {
                    query: this.unsplashSearchTerm,
                    page: this.page
                }
            }).then(response => {
                this.swappingScreens = true;

                setTimeout(() => {
                    this.unsplashImages = this.searchResults = response.data.results;

                    if (this.unsplashImages.length > 0) {
                        this.rememberSearchResult(response.data.results);

                        Vue.nextTick(() => {
                            this.masonry = utils.masonry(this.$refs.masonry, {layoutOnImagesLoaded: false});
                            this.initLightbox();
                        })
                    }

                    this.toggleScreen('search');
                    this.swappingScreens = false;
                    this.$refs.searchButton.classList.remove('is-loading');
                }, 300);
            }).catch(error => {
                let errors = error.response.data.errors;
                this.$refs.searchButton.classList.remove('is-loading');
            });
        },

        getImageRatio(image) {
            return `${image.height / image.width * 100}%`
        },

        initLightbox() {
            this.lightbox = new utils.Lightbox(this.$refs.masonry, 'unsplash-picker', {
                photoSwipe: {
                    arrowKeys: false,
                    allowPanToNext: false,
                    counterEl: false,
                    arrowEl: false,
                    zoomEl: false,
                    captionEl: true,
                    barsSize: {
                        top: 45, bottom: 45
                    },
                    getThumbBoundsFn: (index) => {
                        var thumb = this.$refs.masonry.children[index+2],
                            pageYScroll = window.pageYOffset || document.documentElement.scrollTop,
                            rect = thumb.getBoundingClientRect();

                        return {x: rect.left, y: rect.top + pageYScroll, w: rect.width};
                    }
                },
                onDestroy: () => {
                    this.$emit('photoswipe-closed')
                }
            });
        },

        translationByLocale(en, others) {
            return Pixenio.userLanguage && Pixenio.userLanguage.locale !== 'en' ? others : en;
        },

        loadMore() {
            this.page++;
            axios.get(`${this.$evilCorp.axiosEndpoint}/unsplash`, {
                params: {
                    query: this.unsplashSearchTerm,
                    page: this.page
                }
            }).then(response => {
                this.unsplashImages = this.searchResults = this.searchResults.concat(response.data.results);

                Vue.nextTick(() => {
                    this.masonry.appended(this.$refs.masonry.querySelectorAll('.masonry-item:not([style])'));
                    this.lightbox.update();
                });

                this.atBottom = false;

            }).catch(error => {
                let errors = error.response.data.errors;
            });
        },

        onScroll({ target: { scrollTop, clientHeight, scrollHeight }}) {
            if (this.historyIsOpened) return;

            // prevent negative scrolling
            scrollTop = Math.max(0, scrollTop);

            const offset = clientHeight;
            if ((scrollTop + clientHeight >= scrollHeight - offset) && !this.atBottom) {
                this.atBottom = true;
                this.loadMore();
            }
        },

        loadFromLocalStorage() {
            this.deleteExpiredSearchResults();

            let lsn = `pxn.unsplash.searchResult.${this.$evilCorp.modelType}.${this.$evilCorp.modelId}.${this.$evilCorp.mediaContext}`;

            let storedSearchResult = LocalStorage.destringify(window.localStorage.getItem(lsn));

            if (!storedSearchResult) return;

            this.swappingScreens = true;

            this.unsplashSearchTerm = storedSearchResult['searchTerm']
            setTimeout(() => {
                this.unsplashImages = this.searchResults = storedSearchResult['result']

                Vue.nextTick(() => {
                    this.masonry = utils.masonry(this.$refs.masonry, {layoutOnImagesLoaded: false});
                    this.initLightbox();
                })

                this.toggleScreen('search');

                this.swappingScreens = false;
            }, 300);
        },

        rememberSearchResult(result) {
            let lsn = `pxn.unsplash.searchResult.${this.$evilCorp.modelType}.${this.$evilCorp.modelId}.${this.$evilCorp.mediaContext}`;

            let expires = new Date();
            expires.setDate( expires.getDate() + 10 );

            let data = {
                expires: expires.getTime(),
                result: result,
                searchTerm: this.unsplashSearchTerm
            };

            window.localStorage.setItem(lsn, LocalStorage.stringify(data));
        },

        rememberUsedImages(images) {
            this.history = uniqBy(images.concat(this.history), 'id');

            let lsn = `pxn.unsplash.history.${this.$evilCorp.siteHashid}`;

            // lifehack: limit history to 50 items - because local storage has limited space
            window.localStorage.setItem(lsn, LocalStorage.stringify(this.history.slice(0, 50)));
        },

        loadHistoryFromLocalStorage() {
            let lsn = `pxn.unsplash.history.${this.$evilCorp.siteHashid}`;
            let storedHistory = LocalStorage.destringify(window.localStorage.getItem(lsn));

            if (storedHistory) {
                this.history = storedHistory;
            }
        },

        browseHistory() {
            this.swappingScreens = true;
            this.clearSelection();

            if (this.historyIsOpened) {
                // history is opened - should switch to search now
                setTimeout(() => {
                    this.unsplashImages = this.searchResults;

                    Vue.nextTick(() => {
                        this.masonry = utils.masonry(this.$refs.masonry, {layoutOnImagesLoaded: false});
                        this.$refs['scroll-panel'].scrollTop = this.lastScrollPositionBeforeSwap;
                        this.initLightbox();
                    });

                    this.toggleScreen('search');
                    this.swappingScreens = false;
                }, 300);
            } else {
                // history is closed - should open now

                this.lastScrollPositionBeforeSwap = this.$refs['scroll-panel'].scrollTop;

                setTimeout(() => {
                    this.unsplashImages = this.history;

                    Vue.nextTick(() => {
                        this.masonry = utils.masonry(this.$refs.masonry, {layoutOnImagesLoaded: false});
                        this.initLightbox();
                    })

                    this.toggleScreen('history');
                    this.swappingScreens = false;
                }, 300);
            }
        },

        toggleScreen(screenName) {
            switch (screenName) {
                case 'search':
                    this.currentScreen = this.searchResults.length > 0 && !this.firstTimeOpened ? 'search' : (this.firstTimeOpened ? 'welcome' : 'no-search-results');
                    break;

                case 'history':
                    this.currentScreen = this.history.length > 0 ? 'history' : 'no-history';
                    break;

                case 'welcome':
                default:
                    this.currentScreen = 'welcome';
                    break;
            }
        },

        deleteExpiredSearchResults() {
            for (const key in window.localStorage) {
                if (window.localStorage.hasOwnProperty(key)) {
                    if (key.startsWith('pxn.unsplash.searchResult.')) {
                        const storedSearchResult = LocalStorage.destringify(window.localStorage.getItem(key));
                        if (storedSearchResult['expires'] < Date.now()) {
                            window.localStorage.removeItem(key);
                        }
                    }
                }
            }
        }
    }
}
</script>
